import { useEffect, useId, useRef } from 'react'
import { streamTypeahead } from 'actions/SearchActions'
import { HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import useRecommendationPlaceIds from './useRecommendationPlaceIds'

interface UseTypeaheadExtractionRefreshParams {
  isExtracting: boolean
  typeaheadId: string | undefined
  query: string | undefined
  typeaheadTypes?: Array<App.SearchPlaceType>
  rollout?: Array<string>
}

/**
 * Dispatches the real (non-preview) extraction request once the results page
 * mounts with extracting=true — covers a page refresh mid-extraction, and a
 * natural-language echo suggestion, which reuses the preview's typeaheadId so
 * streamingData already exists even though no real request was ever sent for it.
 */
export function useTypeaheadExtractionRefresh({
  isExtracting,
  typeaheadId,
  query,
  typeaheadTypes = HOTEL_SEARCH_TYPEAHEAD_TYPES,
  rollout,
}: UseTypeaheadExtractionRefreshParams): void {
  const dispatch = useAppDispatch()
  const streamingData = useAppSelector(state =>
    typeaheadId ? state.search.typeaheadStreamingData[typeaheadId] : undefined,
  )
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const recentPlaceIds = useRecommendationPlaceIds()
  const streamingListenerId = useId()
  const hasDispatchedRef = useRef(false)

  useEffect(() => {
    // !streamingData alone would miss the echo-suggestion case: streamingData
    // already exists (from the preview) but was never for a real request.
    if (isExtracting && typeaheadId && query && !streamingData?.isComplete && !hasDispatchedRef.current) {
      hasDispatchedRef.current = true
      dispatch(
        streamTypeahead({
          input: query,
          typeaheadId,
          listenerId: streamingListenerId,
          types: typeaheadTypes,
          limit: 9,
          isLuxPlusMember: canViewLuxPlusBenefits,
          temporaryDoNotExtract: false,
          rollout,
          recentPlaceIds,
        }),
      )
    }

    // Reset the ref when typeaheadId changes or extraction completes
    if (!isExtracting || !typeaheadId) {
      hasDispatchedRef.current = false
    }
  }, [isExtracting, typeaheadId, query, streamingData, dispatch, canViewLuxPlusBenefits, streamingListenerId, typeaheadTypes, rollout, recentPlaceIds])
}
